import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "../context/auth";

export function AccountPage() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const { user } = useAuth();
	const [showConfirm, setShowConfirm] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [cooldownDate, setCooldownDate] = useState<string | null>(null);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleteSubmitting, setDeleteSubmitting] = useState(false);
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [deletePending, setDeletePending] = useState(false);
	const [deleteSuccess, setDeleteSuccess] = useState(false);
	const [deletePendingExpiry, setDeletePendingExpiry] = useState<string | null>(
		null,
	);

	useEffect(() => {
		if (!user?.lastDataExportAt) return;
		const nextAvailable = user.lastDataExportAt + 7 * 24 * 60 * 60 * 1000;
		if (Date.now() < nextAvailable) {
			setCooldownDate(new Date(nextAvailable).toISOString());
		}
	}, [user?.lastDataExportAt]);

	useEffect(() => {
		async function checkDeletionStatus() {
			try {
				const res = await fetch(
					`/api/tenant/${workspaceSlug}/deletion-status`,
					{
						credentials: "include",
					},
				);
				if (res.ok) {
					const data = (await res.json()) as {
						pending: boolean;
						expiresAt?: string;
					};
					if (data.pending) {
						setDeletePending(true);
						if (data.expiresAt) {
							setDeletePendingExpiry(data.expiresAt);
						}
					}
				}
			} catch {
				// Silently fail — status check is non-critical
			}
		}
		checkDeletionStatus();
	}, [workspaceSlug]);

	async function handleExport() {
		setSubmitting(true);
		setError(null);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/data-export`, {
				credentials: "include",
			});
			if (res.status === 429) {
				const body = (await res.json()) as { nextAvailableAt: string };
				setCooldownDate(body.nextAvailableAt);
				setShowConfirm(false);
				return;
			}
			if (!res.ok) {
				setError("Failed to export data. Please try again.");
				return;
			}
			const blob = await res.blob();
			const disposition = res.headers.get("Content-Disposition") ?? "";
			const filenameMatch = disposition.match(/filename="(.+?)"/);
			const filename = filenameMatch?.[1] ?? "data-export.json";
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
			setShowConfirm(false);
			setCooldownDate(
				new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
			);
		} catch {
			setError("Network error. Please try again.");
		} finally {
			setSubmitting(false);
		}
	}

	async function handleDeleteRequest() {
		setDeleteSubmitting(true);
		setDeleteError(null);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/delete-request`, {
				method: "POST",
				credentials: "include",
			});
			if (res.status === 409) {
				setDeletePending(true);
				setShowDeleteConfirm(false);
				return;
			}
			if (!res.ok) {
				setDeleteError("Failed to request deletion. Please try again.");
				return;
			}
			setDeleteSuccess(true);
			setShowDeleteConfirm(false);
		} catch {
			setDeleteError("Network error. Please try again.");
		} finally {
			setDeleteSubmitting(false);
		}
	}

	return (
		<div className="max-w-sm mx-auto mt-8">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">Account</h1>

			<div className="bg-white rounded-lg shadow p-6">
				<h2 className="text-lg font-semibold text-gray-900 mb-2">
					Export My Data
				</h2>
				<p className="text-sm text-gray-600 mb-4">
					Download all your personal data as a JSON file. You can request one
					export every 7 days.
				</p>

				{error && <p className="text-sm text-red-600 mb-4">{error}</p>}

				{cooldownDate && (
					<p className="text-sm text-amber-700 mb-4">
						Next export available on{" "}
						{new Date(cooldownDate).toLocaleDateString(undefined, {
							year: "numeric",
							month: "long",
							day: "numeric",
						})}
					</p>
				)}

				{!showConfirm ? (
					<button
						type="button"
						onClick={() => setShowConfirm(true)}
						disabled={!!cooldownDate}
						className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
					>
						Export My Data
					</button>
				) : (
					<div className="space-y-3">
						<p className="text-sm text-gray-700">
							This will download all your personal data as a JSON file.
							Continue?
						</p>
						<div className="flex gap-3">
							<button
								type="button"
								onClick={handleExport}
								disabled={submitting}
								className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
							>
								{submitting ? "Exporting..." : "Confirm"}
							</button>
							<button
								type="button"
								onClick={() => setShowConfirm(false)}
								disabled={submitting}
								className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 disabled:opacity-50"
							>
								Cancel
							</button>
						</div>
					</div>
				)}
			</div>

			<div className="bg-white rounded-lg shadow p-6 mt-6">
				<h2 className="text-lg font-semibold text-gray-900 mb-2">
					Delete My Account
				</h2>
				<p className="text-sm text-gray-600 mb-4">
					Permanently delete your account and all associated data. This action
					cannot be undone.
				</p>

				{deleteError && (
					<p className="text-sm text-red-600 mb-4">{deleteError}</p>
				)}

				{deleteSuccess ? (
					<p className="text-sm text-green-700">
						Deletion requested. Check your email to confirm.
					</p>
				) : deletePending ? (
					<div>
						<p className="text-sm text-amber-700 mb-2">
							A deletion request is pending. Check your email to confirm.
						</p>
						{deletePendingExpiry && (
							<p className="text-sm text-gray-500">
								Expires on{" "}
								{new Date(deletePendingExpiry).toLocaleDateString(undefined, {
									year: "numeric",
									month: "long",
									day: "numeric",
								})}
							</p>
						)}
					</div>
				) : !showDeleteConfirm ? (
					<button
						type="button"
						onClick={() => setShowDeleteConfirm(true)}
						className="w-full bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700"
					>
						Delete My Account
					</button>
				) : (
					<div className="space-y-3">
						<p className="text-sm text-gray-700">
							This will permanently delete your account and all associated data.
							This action cannot be undone. Are you sure?
						</p>
						<div className="flex gap-3">
							<button
								type="button"
								onClick={handleDeleteRequest}
								disabled={deleteSubmitting}
								className="flex-1 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 disabled:opacity-50"
							>
								{deleteSubmitting ? "Requesting..." : "Confirm"}
							</button>
							<button
								type="button"
								onClick={() => setShowDeleteConfirm(false)}
								disabled={deleteSubmitting}
								className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 disabled:opacity-50"
							>
								Cancel
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
